// Annual Review — shown after each End of Year. A year of asset cashflow has just
// settled into cash; this screen summarises the year and one mentor weighs in.

import { motion } from 'framer-motion';
import { useEffect, useRef } from 'react';
import { Card, Eyebrow, Button, Money, money } from '../ui/kit.js';
import { useLife } from './store.js';
import { useVoice } from '../voice/VoiceContext.js';

export type YearReview = {
  yearFrom: number;
  yearTo: number;
  incomeEarned: number; // annual passive income realised
  invested: number; // total value working in assets
  wealthChange: number; // total wealth (net worth + cash) change over the year
  freedomMove: number; // Freedom Meter % change over the year
};

export function AnnualReview({ review, onContinue }: { review: YearReview; onContinue: () => void }) {
  const mentors = useLife().mastermind;
  const lead = mentors[0];
  const comment =
    review.wealthChange > 0 && review.freedomMove > 0
      ? 'HUGE season. No cap. Keep stacking.'
      : review.wealthChange > 0
        ? 'Bag secured. Now buy MONEY MACHINES.'
        : review.wealthChange < 0
          ? 'Rough season. Cut costs. Bounce back.'
          : 'Steady. Stack MONEY WHILE YOU SLEEP.';

  // MIKO SPEAKS — narrate the season recap once: age up + the verdict line.
  const { speak } = useVoice();
  const spokeRef = useRef(false);
  useEffect(() => {
    if (spokeRef.current) return;
    spokeRef.current = true;
    void speak(`Season recap! You're ${review.yearTo} now. ${comment}`);
  }, [speak, review.yearTo, comment]);

  return (
    <Overlay>
      <Card>
        <div style={{ textAlign: 'center', marginBottom: 10 }}>
          <Eyebrow color="var(--brand)">SEASON RECAP</Eyebrow>
          <div style={{ fontSize: 28, fontWeight: 900, color: 'var(--text)' }}>
            AGE {review.yearFrom} → {review.yearTo}
          </div>
        </div>

        <div style={{ display: 'grid', gap: 8 }}>
          <Row label="MONEY WHILE YOU SLEEP" hint="a whole year of it" value={review.incomeEarned} tone="up" />
          <Row label="LOCKED IN MACHINES" hint="working for you" value={review.invested} />
          <Row label="EMPIRE VALUE CHANGE" hint="vs last season" value={review.wealthChange} signed />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '6px 0' }}>
            <span>
              <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--text)' }}>FREEDOM LEVEL</span>
              <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-3)' }}>moved this season</span>
            </span>
            <span className="bk-tnum" style={{ fontWeight: 900, fontSize: 17, color: review.freedomMove >= 0 ? 'var(--up)' : 'var(--down)' }}>
              {review.freedomMove >= 0 ? '+' : '−'}{Math.abs(Math.round(review.freedomMove))}%
            </span>
          </div>
        </div>

        {/* mentor comment */}
        <div style={{ marginTop: 14, display: 'flex', gap: 12, alignItems: 'flex-start', background: 'var(--brand-soft)', border: '1px solid var(--brand)', borderRadius: 16, padding: 14 }}>
          <span style={{ fontSize: 28, flexShrink: 0 }}>{lead?.icon ?? '🧑‍🏫'}</span>
          <span>
            <span style={{ display: 'block', fontSize: 11, fontWeight: 800, color: 'var(--brand)', marginBottom: 2 }}>{lead?.name ?? 'Your guide'}</span>
            <span style={{ fontSize: 13.5, color: 'var(--text)', lineHeight: 1.5, fontStyle: 'italic' }}>“{comment}”</span>
          </span>
        </div>

        <div style={{ marginTop: 16 }}>
          <Button tone="primary" size="lg" full onClick={onContinue}>LET’S GO →</Button>
        </div>
      </Card>
    </Overlay>
  );
}

function Row({ label, hint, value, tone, signed }: { label: string; hint: string; value: number; tone?: 'up'; signed?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '6px 0' }}>
      <span>
        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{label}</span>
        <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-3)' }}>{hint}</span>
      </span>
      {signed ? <Money value={value} signed size={17} /> : (
        <span className="bk-tnum" style={{ fontWeight: 900, fontSize: 17, color: tone === 'up' ? 'var(--up)' : 'var(--text)' }}>{money(value)}</span>
      )}
    </div>
  );
}

export function Overlay({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 78, display: 'grid', placeItems: 'center', padding: 18, background: 'rgba(10,14,26,0.6)', backdropFilter: 'blur(7px)', WebkitBackdropFilter: 'blur(7px)' }}>
      <motion.div initial={{ y: 16, opacity: 0, scale: 0.97 }} animate={{ y: 0, opacity: 1, scale: 1 }} style={{ width: '100%', maxWidth: 460 }}>
        {children}
      </motion.div>
    </div>
  );
}
